import { createRef, FC, useRef } from 'react';

import classNames from 'classnames';
import { getImageSize } from 'utils/clickme';
import { BannerImageCropType } from 'utils/constants/banner';

import { Cell, CellText, Layer, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { InfoCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { TipContent } from 'components/common/BannerImg/CropImageModal/ImageContent/Description/TipContent';
import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';

import styles from 'components/common/BannerImg/CropImageModal/ImageContent/Description/Description.module.less';

const CROP_TYPE_WITH_FULL_NAME = [BannerImageCropType.VK, BannerImageCropType.YandexAndVK];

const LOGO_CLASS = {
    [BannerImageCropType.HH]: classNames(styles.contentLogo, styles['content-logo_hh']),
    [BannerImageCropType.VK]: classNames(styles.contentLogo, styles['content-logo_vk']),
    [BannerImageCropType.YandexAndVK]: classNames(styles.contentLogo, styles['content-logo_yandex-and-vk']),
};

const TrlKeys = {
    sources: {
        [BannerImageCropType.HH]: 'AdCreate.AdForm.CropImage.sources.TARGET_DEFAULT',
        [BannerImageCropType.VK]: 'AdCreate.AdForm.CropImage.sources.MY_TARGET_DEFAULT',
        [BannerImageCropType.YandexAndVK]: 'AdCreate.AdForm.CropImage.sources.VK_DEFAULT',
    },
    size: 'AdCreate.AdForm.CropImage.sources.sizeMagritte',
};

interface DescriptionProps {
    cropType: BannerImageCropType;
    disabled: boolean;
}

export const Description: FC<DescriptionProps> = ({ cropType, disabled }) => {
    const [tooltipVisible, setTooltipVisibleOn, setTooltipVisibleOff] = useOnOffState(false);

    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const activatorRefs = useRef<Record<BannerImageCropType, React.RefObject<HTMLDivElement>>>({
        [BannerImageCropType.HH]: createRef<HTMLDivElement>(),
        [BannerImageCropType.VK]: createRef<HTMLDivElement>(),
        [BannerImageCropType.YandexAndVK]: createRef<HTMLDivElement>(),
    });

    return (
        <div>
            <div className={styles.contentDescription}>
                <div
                    className={classNames(LOGO_CLASS[cropType], {
                        [styles.contentLogoDisabled]: disabled,
                    })}
                />
                <div>
                    {CROP_TYPE_WITH_FULL_NAME.includes(cropType) ? (
                        <Cell
                            width="fit-content"
                            align="center"
                            gap={6}
                            right={
                                <InfoCircleOutlinedSize16
                                    initialColor="secondary"
                                    ref={activatorRefs.current[cropType]}
                                    onClick={setTooltipVisibleOn}
                                    onMouseEnter={isMobile ? undefined : setTooltipVisibleOn}
                                    onMouseLeave={isMobile ? undefined : setTooltipVisibleOff}
                                />
                            }
                        >
                            <CellText typography="subtitle-1-semibold">{trls(TrlKeys.sources[cropType])}</CellText>
                        </Cell>
                    ) : (
                        <Text typography="subtitle-1-semibold">{trls(TrlKeys.sources[cropType])}</Text>
                    )}
                    <VSpacing default={6} />
                    <Text typography="label-3-regular" style="secondary">
                        {trls(TrlKeys.size, getImageSize(cropType))}
                    </Text>
                </div>
            </div>
            <Layer layer="modal">
                <TooltipCommon
                    activatorRef={activatorRefs.current[cropType]}
                    tooltipContent={<TipContent cropType={cropType} />}
                    bottomSheetTitle={trls(TrlKeys.sources[cropType])}
                    placement="bottom-center"
                    visible={tooltipVisible}
                    onClose={setTooltipVisibleOff}
                />
            </Layer>
        </div>
    );
};
